import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, Mail, Settings2 } from "lucide-react";
import { toast } from "sonner";
import {
  getNotificationSettings,
  saveNotificationSettings,
  NotificationSettings as NotificationSettingsType,
} from "@/hooks/useEmployeeNotification";

const categoryLabels: Record<string, string> = {
  stammdaten: "Stammdaten",
  gehalt: "Gehaltsdaten",
  bonus: "Bonusdaten",
  benefits: "Benefits",
  stellenbeschreibung: "Stellenbeschreibung",
  kfz_kosten: "KFZ-Kosten",
  dokumente: "Dokumente",
  zugangsdaten: "Zugangsdaten",
  stunden_abwesenheit: "Stunden/Abwesenheit",
  arbeitskleidung: "Arbeitskleidung",
  werkzeug: "Werkzeug und Arbeitsmaterial",
  sonstige_kosten: "Sonstige Kosten",
  bewertung: "Bewertung",
  mitarbeitergespraech: "Mitarbeitergespräch",
  annehmlichkeiten: "Annehmlichkeiten",
  handy: "Mobiltelefon/Handy",
};

export const NotificationSettings = () => {
  const [settings, setSettings] = useState<NotificationSettingsType>(getNotificationSettings());

  useEffect(() => {
    setSettings(getNotificationSettings());
  }, []);

  const handleGlobalToggle = (enabled: boolean) => {
    const newSettings = { ...settings, enabled };
    setSettings(newSettings);
    saveNotificationSettings(newSettings);
    toast.success(enabled ? "Benachrichtigungen aktiviert" : "Benachrichtigungen deaktiviert");
  };

  const handleCategoryToggle = (category: keyof NotificationSettingsType["categories"], enabled: boolean) => {
    const newSettings = {
      ...settings,
      categories: { ...settings.categories, [category]: enabled },
    };
    setSettings(newSettings);
    saveNotificationSettings(newSettings);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            E-Mail-Benachrichtigungen
          </CardTitle>
          <CardDescription>
            Mitarbeiter werden per E-Mail benachrichtigt, wenn Änderungen an ihren Daten vorgenommen werden.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-primary" />
              <div>
                <Label htmlFor="global-notifications" className="text-base font-medium">
                  Automatische Benachrichtigungen
                </Label>
                <p className="text-sm text-muted-foreground">
                  Aktivieren Sie diese Option, um automatische E-Mail-Benachrichtigungen zu senden
                </p>
              </div>
            </div>
            <Switch
              id="global-notifications"
              checked={settings.enabled}
              onCheckedChange={handleGlobalToggle}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Kategorien
          </CardTitle>
          <CardDescription>
            Wählen Sie aus, bei welchen Änderungen Benachrichtigungen gesendet werden sollen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {Object.entries(categoryLabels).map(([key, label]) => (
              <div
                key={key}
                className="flex items-center justify-between py-2 border-b last:border-0"
              >
                <Label htmlFor={`category-${key}`} className="cursor-pointer">
                  {label}
                </Label>
                <Switch
                  id={`category-${key}`}
                  checked={settings.categories[key as keyof NotificationSettingsType["categories"]]}
                  onCheckedChange={(checked) =>
                    handleCategoryToggle(key as keyof NotificationSettingsType["categories"], checked)
                  }
                  disabled={!settings.enabled}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
